import { AWTEventData } from '@aria/webjs-sdk/dist/src/common/DataModels';
import { CommonAdapter, getAdapter } from 'owa-addins-adapters';
import { getUserTypeString } from 'owa-addins-wrapper-utils';
import { isPersistedAddin } from 'owa-addins-persistent';
import { isItemSendEvent } from 'owa-addins-store';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { getUserConfiguration } from 'owa-session-store';
import { getBrowserInfo } from 'owa-user-agent';
import {
    IAddinCommand,
    AddinCommand,
    ContextualAddinCommand,
    getEntryPointForControl,
    ExtensionEntryPointEnum,
} from 'owa-addins-store';
import {
    AppState,
    AudienceGroup,
    LaunchReason,
    TenantGroup,
    CommandPlacement,
    ActionType,
    OTelSchema,
} from '../enums/otelSchema';
import { getClientId, getClientVersion, getLogicalRing } from 'owa-config';

export function addOTelColumnData(
    event: AWTEventData,
    addinCommand: IAddinCommand,
    hostItemIndex: string,
    controlId: string
) {
    const config = getUserConfiguration();
    event.properties[OTelSchema.AppName] = 'Outlook';
    event.properties[OTelSchema.AppPlatform] = 'Web';
    event.properties[OTelSchema.AppVersion] = getClientVersion();
    event.properties[OTelSchema.ReleaseAudienceGroup] = getAudienceGroup();
    event.properties[OTelSchema.ReleaseAudience] = getLogicalRing();
    event.properties[OTelSchema.UserTenantId] = config.SessionSettings.ExternalDirectoryTenantGuid;
    event.properties[OTelSchema.UserTenantGroup] = getTenantGroup();
    event.properties[OTelSchema.UserPrimaryIdentitySpace] = getUserIdentitySpace();
    event.properties[OTelSchema.UserPrimaryIdentityHash] = config.SessionSettings.UserPuid;
    event.properties[OTelSchema.BrowserName] = getBrowserInfo().browser;
    event.properties[OTelSchema.BrowserVersion] = getBrowserInfo().browserVersion.join('.');
    event.properties[OTelSchema.SessionId] = toGuid(getClientId());
    event.properties[OTelSchema.EventTime] = new Date().toISOString();
    event.properties[OTelSchema.SdxAppState] = getAppState(hostItemIndex);
    event.properties[OTelSchema.DeviceOSVersion] = 'N/A';
    event.properties[OTelSchema.DeviceOSBuild] = 'N/A';
    event.properties[OTelSchema.SdxLaunchReason] = getLaunchReason(
        addinCommand as AddinCommand,
        controlId
    );
    event.properties[OTelSchema.SdxCommandPlacement] = getCommandPlacement(
        addinCommand as AddinCommand,
        hostItemIndex,
        controlId
    );
    event.properties[OTelSchema.SdxActionType] = getActionType(controlId);
    event.properties[OTelSchema.SdxExEnv] = getLogicalRing();
}
function getAudienceGroup(): AudienceGroup {
    switch (getLogicalRing()) {
        case 'Dogfood':
            return AudienceGroup.Dogfood;
        case 'Microsoft':
            return AudienceGroup.MSIT;
        default:
            return AudienceGroup.Production;
    }
}
function getTenantGroup(): TenantGroup {
    const userType = getUserTypeString();
    return userType === 'outlookCom' || userType === 'gmail'
        ? TenantGroup.Consumer
        : TenantGroup.Commercial;
}
function getAppState(hostItemIndex: string): AppState {
    const adapter = getAdapter(hostItemIndex) as CommonAdapter;
    const mode = adapter.mode;
    switch (mode) {
        case ExtensibilityModeEnum.MessageRead:
            return AppState.MessageRead;
        case ExtensibilityModeEnum.MessageCompose:
            return AppState.MessageCompose;
        case ExtensibilityModeEnum.AppointmentAttendee:
            return AppState.AppointmentAttendee;
        case ExtensibilityModeEnum.AppointmentOrganizer:
            return AppState.AppointmentOrganizer;
        default:
            return AppState.Unknown;
    }
}
function getLaunchReason(addinCommand: AddinCommand, controlId: string): LaunchReason {
    if (isItemSendEvent(addinCommand.extension.Id, controlId)) {
        return LaunchReason.Auto;
    }
    // if an add-in is pinned, we assume it auto-opened
    if (isPersistedAddin(addinCommand)) {
        return LaunchReason.Auto;
    }
    return LaunchReason.Manual;
}
function getCommandPlacement(
    addinCommand: AddinCommand,
    hostItemIndex: string,
    controlId: string
): CommandPlacement {
    if (isItemSendEvent(addinCommand.extension.Id, controlId)) {
        return CommandPlacement.OnSend;
    }

    if (isPersistedAddin(addinCommand)) {
        return CommandPlacement.Persistent;
    }
    const contextual = (addinCommand as IAddinCommand) as ContextualAddinCommand;
    if (contextual.detectedEntity) {
        return CommandPlacement.Contextual;
    }
    const adapter = getAdapter(hostItemIndex) as CommonAdapter;
    const placement = adapter.isAddinPinned(addinCommand.extension.Id)
        ? CommandPlacement.Ribbon
        : CommandPlacement.Overflow;
    return placement;
}
function getActionType(controlId: string): ActionType {
    const entrypoint = getEntryPointForControl(controlId);
    switch (entrypoint) {
        case ExtensionEntryPointEnum.TaskPane:
            return ActionType.ShowTaskpane;
        case ExtensionEntryPointEnum.UILess:
            return ActionType.ExecuteFunction;
        case ExtensionEntryPointEnum.Contextual:
            return ActionType.Contextual;
        case ExtensionEntryPointEnum.Dialog:
            return ActionType.Dialog;
        case ExtensionEntryPointEnum.Unknown:
            return ActionType.Unknown;
    }
}

function getUserIdentitySpace(): string {
    const userType = getUserTypeString();
    switch (userType) {
        case 'outlookCom':
            return 'MsaPuid';
        case 'office365':
            return 'OrgIdPuid';
        default:
            return 'Unknown';
    }
}

function toGuid(guid: string) {
    if (guid !== undefined) {
        return `${guid.slice(0, 8)}-${guid.slice(8, 12)}-${guid.slice(12, 16)}-${guid.slice(
            16,
            20
        )}-${guid.slice(20)}`.toLowerCase();
    }
    return guid;
}
